import Base from '../base';
import translate from '../../utils/webcomponents/translate';

const paginationAPI = '/api/webinars/from-cursor';

class WebinarPagination extends Base {
	start() {
		this.grid = this.refs.grid;
		this.button = this.refs.load;
		this.status = this.options.status;
		this.pageSize = Number(this.options.pageSize) || 3;
		this.cursor = this.options.initialCursor || '';
		this.cellClass = this.options.cellClass || '';
		this.cardClass = this.options.cardClass || '';
		this.hasMore = this.options.hasMore;
		this.isLoading = false;
		this._abort = null;
		this.liveRegion = document.getElementById(this.options.liveRegionId) || null;

		if (!this.grid || !this.button || !this.status) return this;

		if (!this.hasMore) {
			this.hideButton();
			return this;
		}

		this.on('click', this.button);
		return this;
	}

	async onClick() {
		if (this.isLoading || !this.hasMore) return;

		// Abort any in-flight request
		if (this._abort) this._abort.abort();
		this._abort = new AbortController();

		this.isLoading = true;
		this.setBusyState(true);

		try {
			const response = await fetch(this.buildRequestUrl(), {
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
				signal: this._abort.signal,
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch webinars: ${response.status}`);
			}

			const data = await response.json();

			if (Array.isArray(data.items) && data.items.length) {
				this.appendItems(data.items);

				const moreWebinarsLoaded = translate(
					'webinarcontent',
					'moreWebinarsLoaded',
					'New webinars have been loaded.',
				);
				this.announce(`${data.items.length} ${moreWebinarsLoaded}`);
			}

			if (data.lastRetrievedSlug) {
				this.cursor = data.lastRetrievedSlug;
			}

			if (data.hasItemsLeft === false) {
				this.hasMore = false;
				this.hideButton();
			}
		} catch (err) {
			if (err.name !== 'AbortError') {
				console.error('Unable to load more webinars', err);

				const errorMessage = translate(
					'webinarcontent',
					'paginationErrorMessage',
					'Sorry, something went wrong while loading more webinars.',
				);

				this.announce(errorMessage);
			}
		} finally {
			this.isLoading = false;
			this.setBusyState(false);
			this._abort = null;
		}
	}

	buildRequestUrl() {
		const params = new URLSearchParams();
		if (this.cursor) params.append('cursor', this.cursor);
		if (this.pageSize) params.append('size', String(this.pageSize));
		const query = params.toString() || '';
		const safeStatus = encodeURIComponent(this.status);

		return `${paginationAPI}/${safeStatus}?${query}`;
	}

	appendItems(items) {
		if (!Array.isArray(items) || items.length === 0 || !this.grid) return;

		const fragment = document.createDocumentFragment();

		for (const markup of items) {
			const cell = document.createElement('div');
			cell.className = this.cellClass;

			const card = document.createElement('div');
			card.className = this.cardClass;
			card.innerHTML = markup;

			cell.append(card);
			fragment.append(cell);
		}

		this.grid.append(fragment);

		this.grid.dispatchEvent(
			new CustomEvent('webinars-appended', {
				detail: { count: items.length },
				bubbles: true,
				composed: true,
			}),
		);
	}

	setBusyState(isBusy) {
		if (this.button) {
			this.button.toggleAttribute('disabled', isBusy);
			this.button.setAttribute('aria-disabled', String(isBusy));
			this.button.classList.toggle('is-disabled', isBusy);
		}

		if (this.grid) {
			this.grid.setAttribute('aria-busy', String(isBusy));
		}
	}

	hideButton() {
		if (!this.button) return;
		const wasFocused = document.activeElement === this.button;

		this.off('click', this.button);
		this.button.remove();
		this.button = null;

		// Remove focus from removed button
		if (wasFocused && this.grid) {
			this.grid.focus();
		}
	}

	announce(message) {
		if (!this.liveRegion || !message) return;
		this.liveRegion.textContent = message;
	}
}

export default WebinarPagination;
